import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  DetailShell,
  HCard,
  HGhostButton,
  HPrimaryButton,
  HSecondaryButton,
  SectionLabel,
  SerifHeading,
  SignalBars,
  SignalLabel,
} from '../h';
import { useFamiliesData } from './useFamiliesData';
import {
  sendIntercomRequest,
  attachIntercomRequest,
  IntercomError,
  IntercomTimeoutError,
  INTERCOM_TIMEOUTS_MS,
} from '../../lib/intercom';
import { encryptWifiSecret } from '../../lib/intercomCrypto';
import {
  WifiCredentialsForm,
  WifiIcon,
  OTHER_SENTINEL,
  effectiveSsidFrom,
} from '../HearthWifi/WifiCredentialsForm';
import { cn } from '../../lib/utils';

/* HearthWifiManagement — /app/hearths/:familyID/wifi (PLAN wifi-change-webapp §4.2–4.4).

   Shows the Hearth's WiFi state and hosts the change-credentials flow over the
   intercom bus.

   There is NO capability gate (decision, 19 Aug): devices auto-update as soon as
   they are online, so the pre-bus window is short. A Hearth on an older build simply
   never answers the keygen; that surfaces as the GENERIC secure-connection copy on
   the change action — generic by decision, because the page cannot know whether the
   cause is an old build, a transient bus failure, or missing rules, and each
   resolves on its own with time. Never blame the Hearth or suggest reloading.

   On entry, every prefetch fires in parallel without blocking the page:
   generate.encryption.keys, get.availableSSIDs, get.currentSSID, get.RSSI. A page
   view therefore issues a keygen even from someone only looking — accepted; the
   device sweeps unused keys by age. If the Hearth is offline nothing is sent, the
   page shows last-known state (the live mirror's RSSI), and the change action is
   disabled with the reason.

   Reload story (PLAN §1.3): the in-flight change request id is persisted in
   sessionStorage next to the session id and cleared when the exchange ends. On
   mount, a persisted id re-attaches to that exchange (attachIntercomRequest) and
   settles on the response already waiting — a refresh mid-change shows the switch
   still running, not an idle form inviting a second submit. On UNMOUNT the change
   exchange is deliberately RETAINED (listener detached, request left running,
   record kept); the prefetch reads are cancelled — their requests are withdrawn.

   The states this page must render distinctly (§4.4): the form; submitted/in
   progress; each §5.2 outcome; and "we haven't heard back" after the timeout —
   NOT the same thing as failure, because at that point the change may well have
   worked. `progress` messages arrive in a clump after the device reconnects, so they
   render as a history under the outcome, never as a live feed. */

const CHANGE_STATE = {
  IDLE: 'idle', // form hidden behind the reveal button
  FORM: 'form',
  SENDING: 'sending', // encrypting + writing the request
  WAITING: 'waiting', // request written; the switch runs (device offline meanwhile)
  DONE: 'done', // terminal result arrived — render outcome
  NO_ANSWER: 'noAnswer', // bus timeout — distinct from success AND failure
};

/* The persisted in-flight change record — sessionStorage, per family, next to the
   session id. Present exactly while a change.credentials exchange is outstanding. */
const INFLIGHT_PREFIX = 'higloIntercomInflightChange:';

/* Stable identity for "no list yet": WifiCredentialsForm reconciles the selection
   whenever the ssids ARRAY IDENTITY changes, so passing a fresh `[]` literal every
   render would churn its effect. One shared instance means "still loading" never
   looks like a list replacement. */
const NO_SSIDS = [];

/* Renders the "What happened" step history after a change. OFF for now (Daniel,
   20 Aug) — the plumbing that produces it is intentionally left in place on both
   sides of the bus, so this is the only line to change to bring it back. */
const SHOW_PROGRESS_LOG = false;

function readInflight(familyID) {
  try {
    const raw = sessionStorage.getItem(INFLIGHT_PREFIX + familyID);
    return raw ? JSON.parse(raw) : null;
  } catch (e) {
    return null;
  }
}
function writeInflight(familyID, record) {
  try {
    sessionStorage.setItem(INFLIGHT_PREFIX + familyID, JSON.stringify(record));
  } catch (e) { /* privacy mode — reload recovery simply unavailable */ }
}
function clearInflight(familyID) {
  try {
    sessionStorage.removeItem(INFLIGHT_PREFIX + familyID);
  } catch (e) { /* ignore */ }
}

export function HearthWifiManagement({ familyID: familyIDProp }) {
  const params = useParams();
  const familyID = familyIDProp || params.familyID;
  const navigate = useNavigate();
  const familiesData = useFamiliesData([familyID]);
  const family = familiesData[familyID] || {};
  const isOnline = !!family.isOnline;

  // ── Prefetched device state ──
  // keyState is a tri-state-plus: 'idle' (nothing attempted yet — first paint must
  // NOT claim failure), 'pending', 'ready', 'failed'.
  const [keyState, setKeyState] = useState('idle');
  const [keyInfo, setKeyInfo] = useState(null); // { publicKey, keyId }
  const [ssids, setSsids] = useState(null); // string[] | null
  const [scanning, setScanning] = useState(false); // a get.availableSSIDs is in flight
  const [currentSsid, setCurrentSsid] = useState(null); // string | null
  const [currentSsidLoaded, setCurrentSsidLoaded] = useState(false);
  // True when the device answered but could NOT read its own association (the
  // `readFailed` wire flag) — rendered as "couldn't check", never as the positive
  // claim "not connected to WiFi".
  const [currentSsidReadFailed, setCurrentSsidReadFailed] = useState(false);
  const [freshRssi, setFreshRssi] = useState(null); // number | null

  // ── Change flow ──
  const [changeState, setChangeState] = useState(CHANGE_STATE.IDLE);
  const [selection, setSelection] = useState(OTHER_SENTINEL);
  const [manualSsid, setManualSsid] = useState('');
  const [password, setPassword] = useState('');
  const [formError, setFormError] = useState('');
  const [outcome, setOutcome] = useState(null); // { outcome, ssid, detail?, reason? }
  const [progressLog, setProgressLog] = useState([]);
  const [attemptedSsid, setAttemptedSsid] = useState('');

  // Prefetch exchanges are CANCELLED on unmount (their requests withdrawn — §7.7);
  // the change exchange is only DETACHED (kept running, record retained).
  const prefetchCancelsRef = useRef(new Set());
  const changeDetachRef = useRef(null);
  useEffect(() => {
    const cancels = prefetchCancelsRef.current;
    return () => {
      cancels.forEach((cancel) => {
        try { cancel(); } catch (e) { /* ignore */ }
      });
      cancels.clear();
      if (changeDetachRef.current) {
        try { changeDetachRef.current(); } catch (e) { /* ignore */ }
        changeDetachRef.current = null;
      }
    };
  }, []);

  /* Contract §7.5: on timeout the caller asks again under a NEW id — a fresh,
     independent request. The prefetch reads (and keygen) get exactly one automatic
     re-issue; a second silence is surfaced as the degraded state instead of looping.
     Duplicates are harmless: the get.* kinds are pure reads and an unused key is
     swept by age on the device. */
  const sendPrefetch = useCallback((topic, kind, payload) => {
    const attempt = () => {
      const handle = sendIntercomRequest(familyID, topic, kind, payload);
      prefetchCancelsRef.current.add(handle.cancel);
      return handle.promise.finally(() => {
        prefetchCancelsRef.current.delete(handle.cancel);
      });
    };
    return attempt().catch((err) => {
      if (err instanceof IntercomTimeoutError) return attempt();
      throw err;
    });
  }, [familyID]);

  /* A fresh key for (re)arming the form. Fired on entry and again after every change
     attempt — a consumed key cannot be reused, and a timed-out one may or may not
     have been consumed, so a fresh one is the only safe assumption. */
  const requestKey = useCallback(() => {
    setKeyState('pending');
    sendPrefetch('security', 'generate.encryption.keys')
      .then((res) => {
        if (res.payload.publicKey && res.payload.keyId) {
          setKeyInfo({ publicKey: res.payload.publicKey, keyId: res.payload.keyId });
          setKeyState('ready');
        } else {
          setKeyState('failed');
        }
      })
      .catch((err) => {
        console.warn('intercom keygen failed', err.message);
        setKeyState('failed');
      });
  }, [sendPrefetch]);

  const refreshCurrentSsid = useCallback(() => {
    sendPrefetch('wifi', 'get.currentSSID')
      .then((res) => {
        // `readFailed` (additive wire flag): the device could not read its own
        // association — "could not check" is a different truth from "not on WiFi".
        setCurrentSsidReadFailed(res.payload.readFailed === true);
        setCurrentSsid(res.payload.ssid ?? null);
        setCurrentSsidLoaded(true);
      })
      .catch((err) => console.warn('intercom get.currentSSID failed', err.message));
    sendPrefetch('wifi', 'get.RSSI')
      .then((res) => {
        if (typeof res.payload.rssiDbm === 'number') setFreshRssi(res.payload.rssiDbm);
      })
      .catch((err) => console.warn('intercom get.RSSI failed', err.message));
  }, [sendPrefetch]);

  const hadListRef = useRef(false); // a picker list has arrived at least once

  /* The ONE place the picker list is fetched — shared by the arming prefetch, the
     manual rescan, and the post-change refresh. Declared BEFORE settleChange, which
     depends on it. Every call costs a real scan on the device: `startScan()` is
     platform-throttled to ~4 calls / 2 min and briefly takes the single radio
     off-channel, which is why this is driven by events and never by a timer. */
  const refreshSsids = useCallback(() => {
    setScanning(true);
    return sendPrefetch('wifi', 'get.availableSSIDs')
      .then((res) => {
        const list = Array.isArray(res.payload.ssids) ? res.payload.ssids : [];
        setSsids(list);
        // Default the picker to the strongest network on the FIRST list only. On any
        // later refresh the customer may already have chosen — WifiCredentialsForm
        // re-points their chosen NAME across the replacement, and a deliberate manual
        // entry must not be yanked back to '0'.
        if (!hadListRef.current && list.length > 0) {
          hadListRef.current = true;
          setSelection((prev) => (prev === OTHER_SENTINEL ? '0' : prev));
        }
      })
      .catch((err) => {
        console.warn('intercom get.availableSSIDs failed', err.message);
        setSsids((prev) => prev || []); // first failure → manual entry; keep a list we already had
      })
      .finally(() => setScanning(false));
  }, [sendPrefetch]);

  /* The shared settle handlers for a change exchange — used by both a fresh submit
     and a reload re-attach. */
  const settleChange = useCallback((ssid) => ({
    onResult: ({ payload, progress }) => {
      clearInflight(familyID);
      changeDetachRef.current = null;
      setProgressLog(progress);
      setOutcome({
        outcome: payload.outcome || 'other',
        ssid: payload.ssid || ssid,
        detail: payload.detail,
      });
      setChangeState(CHANGE_STATE.DONE);
      setPassword(''); // plaintext must not outlive the exchange
      if (payload.outcome === 'connected') {
        refreshCurrentSsid();
        // The picker list was scanned from the OLD network's vantage point, so it is
        // now stale in the one case where the customer is most likely to look at it
        // again. Re-scan from where the Hearth actually ended up.
        refreshSsids();
      } else {
        // Failure paths roll back — show which network the Hearth settled on, and
        // re-scan once it has, for the same reason.
        setTimeout(() => {
          refreshCurrentSsid();
          refreshSsids();
        }, 4000);
      }
    },
    onError: (err) => {
      clearInflight(familyID);
      changeDetachRef.current = null;
      setPassword('');
      if (err instanceof IntercomTimeoutError) {
        // §4.4: distinct from success AND failure — the change may have worked and
        // the answer just hasn't reached us.
        setChangeState(CHANGE_STATE.NO_ANSWER);
      } else if (err instanceof IntercomError) {
        setOutcome({ outcome: 'busError', reason: err.reason, ssid });
        setChangeState(CHANGE_STATE.DONE);
      } else {
        console.warn('change.credentials failed', err);
        setOutcome({ outcome: 'busError', reason: 'writeFailed', ssid });
        setChangeState(CHANGE_STATE.DONE);
      }
    },
  }), [familyID, refreshCurrentSsid, refreshSsids]);

  /* Reload recovery (PLAN §1.3): a persisted id means a change exchange is still
     outstanding — re-attach and settle on whatever is (or arrives) in the outbox.
     Runs once per familyID; independent of the online flag, because the terminal
     response may already be waiting even if the Hearth has since gone offline. */
  const reattachedRef = useRef(null);
  useEffect(() => {
    if (!familyID || reattachedRef.current === familyID) return;
    reattachedRef.current = familyID;
    const record = readInflight(familyID);
    if (!record || !record.id) return;

    const remainingMs = Math.max(
      (record.startedAtMs || 0) + (record.timeoutMs || 0) - Date.now(),
      15000, // grace: if the answer is already waiting it settles instantly anyway
    );
    setAttemptedSsid(record.ssid || '');
    setChangeState(CHANGE_STATE.WAITING);
    const handlers = settleChange(record.ssid || '');
    const attached = attachIntercomRequest(familyID, record.id, {
      timeoutMs: remainingMs,
      endpointLabel: 'wifi/change.credentials',
      onProgress: (p) => setProgressLog((prev) => [...prev, p]),
    });
    changeDetachRef.current = attached.stop;
    attached.promise.then(handlers.onResult).catch(handlers.onError);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [familyID]);

  /* Entry prefetch — all four calls in parallel, never blocking the page (§4.2).
     Fired when the Hearth is seen online, and RE-ARMED whenever it drops offline —
     so a Hearth that goes away and comes back gets fresh state without a manual
     reload. The latch guards StrictMode's double-invoke and same-state re-renders
     from re-issuing keygens. */
  const prefetchedRef = useRef(null); // familyID the CURRENT online spell prefetched

  useEffect(() => {
    if (!familyID) return;
    if (!isOnline) {
      // Offline: disarm so the next online spell prefetches again.
      if (prefetchedRef.current === familyID) prefetchedRef.current = null;
      return;
    }
    if (prefetchedRef.current === familyID) return;
    prefetchedRef.current = familyID;
    requestKey();
    refreshCurrentSsid();
    refreshSsids();
  }, [familyID, isOnline, requestKey, refreshCurrentSsid, refreshSsids]);

  const effectiveSsid = effectiveSsidFrom(ssids || NO_SSIDS, selection, manualSsid);

  // With no capability gate, this copy IS the diagnosis surface for a failed keygen —
  // and the cause is not knowable from here: an old build never answers, but a device
  // answering error/internal demonstrably carries the bus, and a rejected write means
  // the rules are missing. GENERIC by decision: no asserted cause, no remedy that can
  // fail. All three real causes resolve on their own with time (auto-update, retry,
  // rules deploy).
  const changeDisabledReason = !isOnline
    ? 'This Hearth is offline, so it can’t receive a WiFi change. To get it online, use the QR flow on the TV’s "No Network Connection" screen.'
    : keyState === 'failed'
      ? 'We couldn’t set up a secure connection with this Hearth. Please try again in a little while.'
      : null;

  const submitChange = async (e) => {
    e.preventDefault();
    const ssid = effectiveSsid.trim();
    if (!ssid) {
      setFormError('Enter your WiFi network name.');
      return;
    }
    if (!keyInfo) {
      setFormError('Still preparing a secure channel — try again in a moment.');
      return;
    }
    setFormError('');
    setChangeState(CHANGE_STATE.SENDING);
    setOutcome(null);
    setProgressLog([]);
    setAttemptedSsid(ssid);

    const usedKey = keyInfo;
    setKeyInfo(null); // single-use — a fresh one is requested after the attempt
    setKeyState('pending');

    let secret;
    try {
      // The password is encrypted IN THE BROWSER to the device's per-exchange key;
      // it never exists in cleartext outside this handler and this component's state.
      secret = await encryptWifiSecret(usedKey.publicKey, password);
    } catch (err) {
      console.warn('encryptWifiSecret failed', err.message);
      setFormError('Couldn’t encrypt the password in this browser. Make sure the page is on HTTPS.');
      setChangeState(CHANGE_STATE.FORM);
      setKeyInfo(usedKey); // the key was never sent — still usable
      setKeyState('ready');
      return;
    }

    setChangeState(CHANGE_STATE.WAITING);
    const handlers = settleChange(ssid);
    try {
      const handle = sendIntercomRequest(
        familyID,
        'wifi',
        'change.credentials',
        { ssid, secret, keyId: usedKey.keyId },
        {
          // §10: progress bunches on reconnect — collected as history, not a feed.
          onProgress: (p) => setProgressLog((prev) => [...prev, p]),
        },
      );
      // Persist the in-flight id NOW (PLAN §1.3) so a reload mid-change re-attaches
      // instead of showing an idle form while the switch is still running.
      writeInflight(familyID, {
        id: handle.id,
        ssid,
        startedAtMs: Date.now(),
        timeoutMs: INTERCOM_TIMEOUTS_MS['wifi/change.credentials'],
      });
      changeDetachRef.current = handle.detach;
      const settled = await handle.promise;
      handlers.onResult(settled);
    } catch (err) {
      handlers.onError(err);
    } finally {
      requestKey(); // re-arm for the next attempt
    }
  };

  const resetToForm = () => {
    setChangeState(CHANGE_STATE.FORM);
    setOutcome(null);
    setProgressLog([]);
    setFormError('');
  };

  // Prefer the live Firestore value: the Hearth refreshes it every ~10 s as a
  // 10-sample average, so the bars track the device while the page is open. The
  // one-shot intercom read only covers a Hearth that hasn't written a sample yet.
  const rssiToShow = family.wifiRssiDbm ?? freshRssi ?? null;
  const changeInFlight =
    changeState === CHANGE_STATE.SENDING || changeState === CHANGE_STATE.WAITING;

  return (
    <DetailShell
      onBack={() => navigate(`/app/hearths/${familyID}`)}
      breadcrumb={family.familyName || 'Hearth'}
    >
      <div className="px-4 pb-6">
        <div className="flex items-start gap-3 mb-4">
          <WifiIcon className="w-6 h-6 text-slate-700 mt-1 flex-shrink-0" />
          <div className="flex-1 min-w-0">
            <SerifHeading size="xl">WiFi</SerifHeading>
            <div className="mt-1 flex items-center gap-1.5 text-[13px] text-slate-600">
              <span
                className={cn(
                  'inline-block w-[6px] h-[6px] rounded-full flex-shrink-0',
                  isOnline ? 'bg-emerald-500' : 'bg-slate-300',
                )}
                aria-hidden="true"
              />
              <span>{isOnline ? 'Online' : offlineText(family)}</span>
            </div>
          </div>
          {isOnline && rssiToShow != null && (
            <span className="mt-2 flex-shrink-0">
              <SignalBars rssiDbm={rssiToShow} />
            </span>
          )}
        </div>

        {/* Connection state card */}
        <SectionLabel className="mb-2">Connection</SectionLabel>
        <HCard className="mb-4">
          <StatusRow
            label="Network"
            value={
              !isOnline
                ? 'Unknown while offline'
                : !currentSsidLoaded
                  ? 'Checking…'
                  : currentSsidReadFailed
                    // "Could not check" is a different truth from "not on WiFi" —
                    // never assert the Hearth is off WiFi from a failed read.
                    ? 'Couldn’t check right now'
                    : currentSsid || 'Not connected to WiFi'
            }
            divider
          />
          <StatusRow
            label="Signal"
            value={
              !isOnline
                ? '-'
                : rssiToShow != null
                  ? <SignalLabel rssiDbm={rssiToShow} />
                  : 'Checking…'
            }
          />
        </HCard>

        {!isOnline && (
          <HCard variant="warn" className="p-4 mb-4">
            <SectionLabel className="mb-1 text-amber-800">
              This Hearth isn&rsquo;t reaching us
            </SectionLabel>
            <p className="text-[13px] text-slate-700 leading-relaxed">
              {changeDisabledReason}
            </p>
          </HCard>
        )}

        {/* Change credentials — revealed by a button (§4.3) */}
        <SectionLabel className="mb-2">Change WiFi network</SectionLabel>

        {changeState === CHANGE_STATE.IDLE && (
          <>
            <HPrimaryButton
              className="w-full"
              disabled={!!changeDisabledReason}
              onClick={() => !changeDisabledReason && setChangeState(CHANGE_STATE.FORM)}
            >
              Change this Hearth&rsquo;s WiFi
            </HPrimaryButton>
            {isOnline && changeDisabledReason && (
              <p className="text-[11px] text-slate-500 leading-relaxed text-center mt-2">
                {changeDisabledReason}
              </p>
            )}
          </>
        )}

        {(changeState === CHANGE_STATE.FORM || changeInFlight) && (
          <form onSubmit={submitChange}>
            <HCard className="p-4 mb-3">
              {/* The list is fetched on arming and after a change, never on a timer
                  (the device's scan is platform-throttled). This is the customer's
                  way to ask for a fresh one — e.g. a network switched on after the
                  page loaded. */}
              <div className="flex items-start justify-between gap-3 mb-4">
                <p className="text-[13px] text-slate-600 leading-relaxed">
                  {ssids && ssids.length > 0
                    ? 'These are the networks your Hearth can see. Pick one and enter that network’s password.'
                    : 'Enter the network’s name exactly as it appears on your router, and its password.'}
                </p>
                <HGhostButton
                  type="button"
                  onClick={refreshSsids}
                  disabled={scanning || changeInFlight || !isOnline}
                  className="shrink-0 -mt-1 -mr-1"
                >
                  {scanning ? 'Scanning…' : 'Rescan'}
                </HGhostButton>
              </div>
              <WifiCredentialsForm
                ssids={ssids || NO_SSIDS}
                selection={selection}
                onSelectionChange={setSelection}
                manualSsid={manualSsid}
                onManualSsidChange={setManualSsid}
                password={password}
                onPasswordChange={setPassword}
                currentSsid={currentSsidLoaded ? currentSsid : null}
                disabled={changeInFlight}
                idPrefix="wm"
              />
              {/* Contract §9.2: picking the network the Hearth is already on is ALLOWED
                  and is treated exactly like any other change — it updates the stored
                  password, which WPA3-transition and multi-PSK routers accept alongside
                  the old one. There is deliberately NO explanatory card for that case
                  (Daniel, 19 Aug): singling it out invited the customer to think it was
                  special or risky, when the flow, the failure handling and the recovery
                  are identical to a change of network. Do not reintroduce one. */}
              {formError && (
                <p className="text-[13px] text-red-600 leading-relaxed mb-3">{formError}</p>
              )}
              {!changeInFlight ? (
                <div className="grid grid-cols-2 gap-2">
                  <HSecondaryButton type="button" onClick={() => setChangeState(CHANGE_STATE.IDLE)}>
                    Cancel
                  </HSecondaryButton>
                  <HPrimaryButton type="submit" disabled={keyState !== 'ready'}>
                    {keyState === 'ready' ? 'Change network' : 'Preparing…'}
                  </HPrimaryButton>
                </div>
              ) : (
                <WaitingCard state={changeState} ssid={attemptedSsid} />
              )}
            </HCard>
          </form>
        )}

        {changeState === CHANGE_STATE.NO_ANSWER && (
          /* Same compact, untinted treatment as OutcomeCard — these two are siblings
             in the same flow and must not differ in weight. */
          <HCard className="p-3 mb-3">
            <p className="text-[13px] font-semibold text-slate-900">
              We haven&rsquo;t heard back
            </p>
            <p className="text-[12.5px] text-slate-600 leading-snug mt-1">
              Your Hearth didn&rsquo;t answer in time. That doesn&rsquo;t mean the
              change failed — it may still have moved to{' '}
              <strong>{attemptedSsid}</strong> and just not told us yet. If the Hearth
              shows as online in a minute or two, the change worked.
            </p>
            {/* No "Check status" button (Daniel, 20 Aug): it fired a real
                get.currentSSID but this card renders none of the result, and during a
                timeout the device is usually offline so the request was swallowed by a
                console.warn — a control that visibly did nothing. The Connection block
                at the top of this page already tracks online state live from Firestore,
                so there is nothing for the customer to ask for. Close leaves the flow
                the same way the outcome card does. */}
            <div className="flex items-center justify-end gap-1 mt-2 -mr-1 -mb-1">
              <HGhostButton type="button" onClick={() => setChangeState(CHANGE_STATE.IDLE)}>
                Close
              </HGhostButton>
              <HPrimaryButton type="button" onClick={resetToForm}>
                Try again
              </HPrimaryButton>
            </div>
          </HCard>
        )}

        {changeState === CHANGE_STATE.DONE && outcome && (
          <OutcomeCard
            outcome={outcome}
            currentSsid={currentSsidLoaded ? currentSsid : null}
            onRetry={resetToForm}
            onClose={() => setChangeState(CHANGE_STATE.IDLE)}
          />
        )}

        {/* §10: progress is a HISTORY rendered once messages land (they bunch on
            reconnect), never a live feed.

            HIDDEN for now (Daniel, 20 Aug) via SHOW_PROGRESS_LOG. Everything that
            produces it is deliberately INTACT — the device still sends `progress`
            responses, `intercom.js` still surfaces them, and `progressLog` is still
            collected. Flip the constant to true to bring the section back. */}
        {SHOW_PROGRESS_LOG && progressLog.length > 0 && changeState === CHANGE_STATE.DONE && (
          <>
            <SectionLabel className="mb-2 mt-4">What happened</SectionLabel>
            <HCard className="p-3 mb-4">
              {progressLog.map((p, i) => (
                <p key={i} className="text-[12px] text-slate-600 leading-relaxed py-0.5">
                  {p.message || `${p.step || ''} ${p.status || ''}`.trim()}
                </p>
              ))}
            </HCard>
          </>
        )}
      </div>
    </DetailShell>
  );
}

function offlineText(family) {
  if (family.hasEverSignedIn === false) return 'Not yet set up';
  return 'Offline';
}

function StatusRow({ label, value, divider }) {
  return (
    <div
      className={cn(
        'px-4 py-3 flex items-center justify-between gap-3',
        divider && 'border-b border-slate-100',
      )}
    >
      <span className="text-[13px] text-slate-500">{label}</span>
      <span className="text-[13px] font-medium text-slate-900 text-right truncate">{value}</span>
    </div>
  );
}

function WaitingCard({ state, ssid }) {
  return (
    <div className="text-center py-2">
      <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-slate-900 mx-auto mb-2" />
      <p className="text-[13px] text-slate-700 font-medium mb-1">
        {state === CHANGE_STATE.SENDING
          ? 'Sending to your Hearth…'
          : `Moving your Hearth to ${ssid}…`}
      </p>
      <p className="text-[12px] text-slate-500 leading-relaxed">
        This can take a couple of minutes — the Hearth goes offline while it
        switches, so don&rsquo;t worry if it disappears briefly.
      </p>
    </div>
  );
}

/* One card per §5.2 outcome — each rendered distinctly (§4.4). */
function OutcomeCard({ outcome, currentSsid, onRetry, onClose }) {
  // `detail` is still captured into state from the response (§3.1 allows additive
  // fields and a future outcome may carry one), but nothing renders it now that
  // "joined but no internet" is its own outcome word.
  const { outcome: kind, ssid, reason } = outcome;

  const spec = (() => {
    switch (kind) {
      case 'connected':
        return {
          tone: 'ok',
          // No heading (Daniel, 20 Aug): "Done" as its own line cost the card a
          // whole row to say what the sentence says anyway. It reads as the first
          // word of the sentence instead.
          title: null,
          body: (
            <>
              Done. Your Hearth is now on <strong>{ssid}</strong> and back online.
            </>
          ),
          retry: false,
        };
      case 'wrongPassword':
        return {
          tone: 'bad',
          title: 'Wrong password',
          // Evidence-based, no restoration promise (MEASUREMENTS.md 0.1): name the
          // network the Hearth is actually on when we know it; otherwise point at
          // the on-screen QR as the way back.
          body: (
            <>
              The password for <strong>{ssid}</strong> wasn&rsquo;t accepted.{' '}
              {currentSsid ? (
                <>The Hearth is back on <strong>{currentSsid}</strong>. </>
              ) : (
                <>If the Hearth can&rsquo;t get back online, it will show a QR code
                on its screen to reconnect. </>
              )}
              Check the password and try again.
            </>
          ),
          retry: true,
        };
      case 'notFound':
        return {
          tone: 'bad',
          title: 'Network not found',
          body: (
            <>
              Your Hearth couldn&rsquo;t see <strong>{ssid}</strong>. Check the name
              for typos, or move the Hearth closer to the router and try again.
            </>
          ),
          retry: true,
        };
      case 'timeout':
        return {
          tone: 'bad',
          title: 'Couldn’t connect',
          body: (
            <>
              The Hearth ran out of attempts connecting to <strong>{ssid}</strong>.{' '}
              {currentSsid ? (
                <>It is back on <strong>{currentSsid}</strong>. </>
              ) : (
                <>If it can&rsquo;t get back online, it will show a QR code on its
                screen to reconnect. </>
              )}
              The password may be wrong, or the signal too weak.
            </>
          ),
          retry: true,
        };
      case 'config':
        return {
          tone: 'bad',
          title: 'Couldn’t apply the settings',
          body: (
            <>
              The Hearth couldn&rsquo;t set up the configuration for{' '}
              <strong>{ssid}</strong>. Try restarting the Hearth, then try again.
            </>
          ),
          retry: true,
        };
      case 'busError':
        return {
          tone: 'bad',
          title: 'Something went wrong',
          body: (
            <>
              The Hearth couldn&rsquo;t process the request
              {reason ? <> ({reason})</> : null}. Try again in a moment.
            </>
          ),
          retry: true,
        };
      case 'noInternet':
        return {
          tone: 'bad',
          title: 'That network has no internet',
          // Deliberately does NOT say the password was wrong: the device associated
          // successfully, so the credential was accepted — what failed was reaching
          // the internet over it. Sending the customer to re-check a password that
          // worked is the wrong next step.
          body: (
            <>
              The Hearth connected to <strong>{ssid}</strong> but couldn&rsquo;t reach
              the internet on it, so it has been put back on{' '}
              {currentSsid ? (
                <strong>{currentSsid}</strong>
              ) : (
                <>the network it was using before</>
              )}
              . Check that network has working internet before trying again.
            </>
          ),
          retry: true,
        };
      case 'other':
      default:
        return {
          tone: 'bad',
          title: 'Something went wrong',
          body: (
            <>
              The change to <strong>{ssid}</strong> didn&rsquo;t complete. Try again
              in a moment.
            </>
          ),
          retry: true,
        };
    }
  })();

  // Deliberately UNTINTED (Daniel, 20 Aug): no emerald/amber fill per outcome. The
  // title already says which outcome this is, and a full-bleed colour wash on a card
  // this small read as louder than the information in it. `spec.tone` is still carried
  // so a future treatment can use it without re-deriving the outcome here.
  const actions = (
    <>
      <HGhostButton type="button" onClick={onClose}>
        Close
      </HGhostButton>
      {spec.retry && (
        <HPrimaryButton type="button" onClick={onRetry}>
          Try again
        </HPrimaryButton>
      )}
    </>
  );

  // ONE layout for every outcome: an optional heading, the sentence, then the
  // actions on their own row. A success simply has no heading — putting Close
  // beside the sentence instead (tried 20 Aug) stranded it across a wide gap and
  // read worse than the line it saved.
  return (
    <HCard className="p-3 mb-3">
      {spec.title && (
        <p className="text-[13px] font-semibold text-slate-900 mb-1">{spec.title}</p>
      )}
      <p className="text-[12.5px] text-slate-600 leading-snug">{spec.body}</p>
      <div className="flex items-center justify-end gap-1 mt-2 -mr-1 -mb-1">{actions}</div>
    </HCard>
  );
}
